import { useCallback, useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { CloseIcon } from "../Icons";

interface OverlayProps {
  open: boolean;
  title: string;
  eyebrow?: string;
  variant?: "dialog" | "sheet";
  className?: string;
  closeIcon?: ReactNode;
  onClose(): void;
  children: ReactNode;
}

const layers: HTMLElement[] = [];
const layerListeners = new Set<() => void>();
export const subscribeOverlayLayers = (listener: () => void) => {
  layerListeners.add(listener);
  return () => { layerListeners.delete(listener); };
};
export const getActiveOverlayPanel = (): HTMLElement | null =>
  layers.at(-1)?.querySelector<HTMLElement>(".overlay-panel") ?? null;
function publishLayers() { for (const listener of layerListeners) listener(); }
const isolation = new Map<HTMLElement, { count: number; previous: string | null }>();
let hadScrollLock = false;

function isolateBackground(root: HTMLElement) {
  const isolated: HTMLElement[] = [];
  // Inert siblings along the ancestor path, leaving the dialog and its backdrop
  // usable without moving existing overlays out of their styling/context owners.
  for (let branch = root; branch.parentElement; branch = branch.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling === branch || !(sibling instanceof HTMLElement)) continue;
      const entry = isolation.get(sibling) ?? { count: 0, previous: sibling.getAttribute("inert") };
      entry.count++;
      isolation.set(sibling, entry);
      sibling.setAttribute("inert", "");
      isolated.push(sibling);
    }
    if (branch.parentElement === document.body) break;
  }
  return () => {
    for (const element of isolated) {
      const entry = isolation.get(element)!;
      if (--entry.count > 0) continue;
      if (entry.previous === null) element.removeAttribute("inert");
      else element.setAttribute("inert", entry.previous);
      isolation.delete(element);
    }
  };
}

function visibleFocusTarget(element: HTMLElement, panel: HTMLElement) {
  if (element.tabIndex < 0 || element.matches(':disabled, input[type="hidden"]') || element.closest("[hidden], [inert]")) return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
    if (current instanceof HTMLDetailsElement && !current.open &&
      !current.querySelector(":scope > summary")?.contains(element)) return false;
    if (current === panel) break;
  }
  return true;
}

export function Overlay({ open, title, eyebrow, variant = "dialog", className = "", closeIcon = <CloseIcon />, onClose, children }: OverlayProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const exitRef = useRef<{ node: HTMLElement; timer: number } | null>(null);
  const snapshotRef = useRef<{ node: HTMLElement; parent: HTMLElement } | null>(null);
  useLayoutEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const captureExit = useCallback((root: HTMLDivElement | null) => {
    if (!root) return;
    // Ref cleanup runs before DOM removal; layout-effect cleanup runs afterward.
    return () => {
      const parent = root.parentElement;
      const panel = root.querySelector<HTMLElement>(".overlay-panel");
      if (!parent || !panel || document.visibilityState === "hidden" ||
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
        root.closest('.effects-off, [data-motion="still"]')) return;
      const bounds = panel.getBoundingClientRect();
      const shell = root.cloneNode(false) as HTMLElement;
      shell.classList.add("overlay-exit");
      shell.setAttribute("inert", "");
      shell.setAttribute("aria-hidden", "true");
      const backdrop = document.createElement("div");
      backdrop.className = "overlay-backdrop";
      const frame = document.createElement("div");
      frame.className = "overlay-panel";
      Object.assign(frame.style, { position: "fixed", left: `${bounds.left}px`, top: `${bounds.top}px`,
        width: `${bounds.width}px`, height: `${bounds.height}px`, maxHeight: "none" });
      shell.append(backdrop, frame);
      snapshotRef.current = { node: shell, parent };
    };
  }, []);

  // Closing unmounts React children immediately. Only an empty visual shell
  // fades, with no retained actions, text, effects or authorization context.
  useLayoutEffect(() => {
    const clearExit = () => {
      if (!exitRef.current) return;
      window.clearTimeout(exitRef.current.timer);
      exitRef.current.node.remove();
      exitRef.current = null;
    };
    clearExit();
    if (!open) {
      const snapshot = snapshotRef.current;
      snapshotRef.current = null;
      if (snapshot?.parent.isConnected) {
        snapshot.parent.append(snapshot.node);
        exitRef.current = { node: snapshot.node, timer: window.setTimeout(clearExit, 150) };
      }
      return;
    }
    snapshotRef.current = null;
  }, [open]);

  useLayoutEffect(() => () => {
    if (exitRef.current) {
      window.clearTimeout(exitRef.current.timer);
      exitRef.current.node.remove();
      exitRef.current = null;
    }
    snapshotRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const root = panel?.parentElement;
    if (!panel || !root) return;
    if (layers.length === 0) hadScrollLock = document.body.classList.contains("overlay-open");
    // Child effects may run before their parent when nested dialogs mount together.
    const childLayer = layers.findIndex(layer => root.contains(layer));
    layers.splice(childLayer < 0 ? layers.length : childLayer, 0, root);
    const releaseIsolation = isolateBackground(root);
    publishLayers();
    const isTop = () => layers.at(-1) === root;
    const focusables = () => Array.from(panel.querySelectorAll<HTMLElement>(
      'button, a[href], input, select, textarea, summary, [contenteditable="true"], [tabindex]',
    )).filter(element => visibleFocusTarget(element, panel));
    let disposed = false;
    queueMicrotask(() => { if (!disposed && isTop()) (focusables()[0] ?? panel).focus(); });
    function onFocusIn() {
      if (isTop() && !panel!.contains(document.activeElement)) (focusables()[0] ?? panel)!.focus();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (!isTop()) return;
      if (event.key === "Escape") { event.preventDefault(); onCloseRef.current(); return; }
      if (event.key !== "Tab") return;
      const candidates = focusables();
      if (candidates.length === 0) { event.preventDefault(); panel?.focus(); return; }
      const first = candidates[0]!;
      const last = candidates[candidates.length - 1]!;
      // Confirmation can remove the focused button while the dialog stays open.
      if (!panel?.contains(document.activeElement) || document.activeElement === panel) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    document.body.classList.add("overlay-open");
    return () => {
      disposed = true;
      const wasTop = isTop();
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      layers.splice(layers.indexOf(root), 1);
      releaseIsolation();
      publishLayers();
      if (layers.length === 0 && !hadScrollLock) document.body.classList.remove("overlay-open");
      const origin = restoreFocusRef.current;
      if (wasTop && origin?.isConnected && !origin.closest("[inert]")) origin.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  return (
    <div ref={captureExit} className={`overlay-root overlay-${variant} ${className}`} role="presentation">
      <button className="overlay-backdrop" type="button" tabIndex={-1} aria-label="Close overlay" onClick={onClose} />
      <div ref={panelRef} className="overlay-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="overlay-header">
          <div>{eyebrow ? <span className="field-eyebrow">{eyebrow}</span> : null}<h2 id={titleId}>{title}</h2></div>
          <button type="button" className="icon-button" aria-label={`Close ${title}`} onClick={onClose}>{closeIcon}</button>
        </header>
        <div className="overlay-content">{children}</div>
      </div>
    </div>
  );
}
